import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import { fetchProperties } from '@/lib/sheetData';
import { submitLead, REQUEST_TYPES } from '@/lib/web3forms';

export const Enquiry = () => {
  const [activeTab, setActiveTab] = useState('buy');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [allProperties, setAllProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '',
    selectedProject: '', selectedListing: '',
    propertyType: '', sellingPrice: ''
  });

  useEffect(() => {
    fetchProperties().then((data) => {
      setAllProperties(data);
      setProjects(data.filter((p) => p.content_type === 'project'));
    }).catch(() => {});
  }, []);

  const handleProjectChange = (projectName) => {
    setFormData({ ...formData, selectedProject: projectName, selectedListing: '' });
    if (projectName) {
      const listings = allProperties.filter(
        (p) => p.content_type === 'listing' && (p.project_name === projectName || p.builder === projectName)
      );
      setFilteredListings(listings);
    } else {
      setFilteredListings([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const interestedIn =
        activeTab === 'buy'
          ? `${formData.selectedListing || formData.selectedProject || 'Buy property'}`
          : `Sell - ${formData.propertyType}${formData.sellingPrice ? ` (Expected: ${formData.sellingPrice})` : ''}`;

      await submitLead({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        interestedIn,
        requestType: REQUEST_TYPES.GENERAL,
        page: 'Home — Enquiry Section',
        extra: {
          'Enquiry Type': activeTab === 'buy' ? 'Buy' : 'Sell',
          'Project': formData.selectedProject || '—',
          'Listing': formData.selectedListing || '—',
          'Property Type (Sell)': formData.propertyType || '—',
          'Expected Selling Price': formData.sellingPrice || '—',
        },
      });

      setSubmitted(true);
      setFormData({ name: '', phone: '', email: '', selectedProject: '', selectedListing: '', propertyType: '', sellingPrice: '' });
      setFilteredListings([]);
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="py-24 md:py-32 bg-background" data-testid="enquiry-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-16">
          <p className="text-xs md:text-sm uppercase tracking-widest text-brand-green mb-4" data-testid="enquiry-subtitle">Get in Touch</p>
          <h2 className="text-4xl md:text-5xl font-heading font-light text-text-primary" data-testid="enquiry-title">Start Your Journey</h2>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} className="bg-white rounded-2xl p-8 md:p-12 max-w-2xl mx-auto shadow-xl border border-brand-green/10">
          <div className="flex p-1 bg-background-secondary rounded-full mb-8 relative" data-testid="enquiry-toggle">
            <motion.div className="absolute top-1 bottom-1 bg-brand-green rounded-full" initial={false} animate={{ left: activeTab === 'buy' ? '4px' : '50%', right: activeTab === 'buy' ? '50%' : '4px' }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} />
            <button onClick={() => setActiveTab('buy')} className={`flex-1 py-3 text-sm font-medium relative z-10 transition-colors ${activeTab === 'buy' ? 'text-white' : 'text-text-secondary'}`} data-testid="buy-tab-button">BUY PROPERTY</button>
            <button onClick={() => setActiveTab('sell')} className={`flex-1 py-3 text-sm font-medium relative z-10 transition-colors ${activeTab === 'sell' ? 'text-white' : 'text-text-secondary'}`} data-testid="sell-tab-button">SELL PROPERTY</button>
          </div>

          <AnimatePresence mode="wait">
            <motion.form key={activeTab} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} onSubmit={handleSubmit} className="space-y-6" data-testid="enquiry-form">
              <input type="text" placeholder="Full Name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary placeholder:text-text-muted" data-testid="enquiry-name-input" />
              <input type="tel" placeholder="Phone Number" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary placeholder:text-text-muted" data-testid="enquiry-phone-input" />
              <input type="email" placeholder="Email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary placeholder:text-text-muted" data-testid="enquiry-email-input" />

              {activeTab === 'buy' ? (
                <>
                  <select required value={formData.selectedProject} onChange={(e) => handleProjectChange(e.target.value)} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary cursor-pointer" data-testid="enquiry-project-select">
                    <option value="" className="bg-white text-text-primary">Select Project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.project_name || p.title} className="bg-white text-text-primary">{p.title}</option>
                    ))}
                  </select>
                  <select required value={formData.selectedListing} onChange={(e) => setFormData({ ...formData, selectedListing: e.target.value })} disabled={!formData.selectedProject} className={`w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light cursor-pointer ${formData.selectedProject ? 'text-text-primary' : 'text-text-muted'}`} data-testid="enquiry-listing-select">
                    <option value="" className="bg-white text-text-primary">{formData.selectedProject ? 'Which Listing' : 'Select a Project first'}</option>
                    {filteredListings.map((l) => (
                      <option key={l.id} value={l.title} className="bg-white text-text-primary">{l.title}{l.bedrooms ? ` (${l.bedrooms})` : ''}{l.price ? ` — ${l.price}` : ''}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <select required value={formData.propertyType} onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary cursor-pointer" data-testid="enquiry-property-type-select">
                    <option value="" className="bg-white text-text-primary">Property Type</option>
                    <option value="Apartment" className="bg-white text-text-primary">Apartment</option>
                    <option value="Villa" className="bg-white text-text-primary">Villa</option>
                  </select>
                  <input type="text" placeholder="Expected Selling Price" required value={formData.sellingPrice} onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })} className="w-full bg-transparent border-b border-background-tertiary pb-3 focus:border-brand-green outline-none transition-colors font-light text-text-primary placeholder:text-text-muted" data-testid="enquiry-budget-input" />
                </>
              )}

              {error && <p className="text-red-500 text-sm" data-testid="enquiry-error">{error}</p>}

              <button type="submit" disabled={submitting} className="w-full bg-brand-green text-white py-4 rounded-lg font-medium tracking-wide hover:bg-brand-gold transition-colors mt-8 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="enquiry-submit-button">
                {submitting ? (<><Loader2 size={18} className="animate-spin" />SUBMITTING...</>) : submitted ? (<><CheckCircle size={18} />ENQUIRY SUBMITTED!</>) : ('SUBMIT ENQUIRY')}
              </button>
              {submitted && (<motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-brand-green text-sm text-center mt-4" data-testid="enquiry-success-message">Thank you! Our team will get back to you shortly.</motion.p>)}
            </motion.form>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
